import { DEFAULT_STRIPE_PRORATION_BEHAVIOR } from "@intelligems/ig-types/dtos/billing/commands/provider-execution";
import type { ModularBundleDiscountProduct } from "@intelligems/ig-utils/billing/modularBundleDiscount";
import type { StripeProrationItemInput } from "@intelligems/ig-utils/billing/stripeProration";
import {
  computeStripeProration,
  prorationDayDisplay,
} from "@intelligems/ig-utils/billing/stripeProration";
import {
  getModularBundleDiscountPercentageForModuleCount,
  resolveModularBundleDiscount,
} from "@intelligems/ig-utils/billing/modularBundleDiscount";

import type { BillingModule, BillingPlan } from "./types";
import {
  buildBillingCatalog,
  currentCadence,
  currentLiveSubscription,
} from "./catalog";
import { monthsPerPeriod } from "./snapshot";
import {
  SMART_PRICING_VARIANTS,
  STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE,
  UNLIMITED_ID,
} from "./constants";
import type {
  ActionQuote,
  BillingAdjustment,
  BillingCycle,
  BillingSnapshot,
  CatalogEntities,
  NamedBillingEntity,
  Quote,
  QuoteCalculation,
  RawSubscriptionAccess,
  RawUpgradeDowngradePath,
  Selection,
} from "./types";

type AccessProduct = RawSubscriptionAccess["access"]["products"][number];

type SubscriptionItem = NonNullable<
  RawSubscriptionAccess["subscriptions"]["active"][number]["items"]
>[number];

// Entity ids are entitlement profile keys, so pass them through for key-based
// classification; a name-only product falls back to name matching, which
// misreads "Smart Pricing Unlimited" as the Unlimited plan.
const toBundleProducts = (
  entities: NamedBillingEntity[]
): ModularBundleDiscountProduct[] =>
  entities.map(({ id, name }) => ({ name, entitlementProfileKey: id }));

const catalogSnapshot = (snapshot: BillingSnapshot): BillingSnapshot => ({
  ...snapshot,
  actualPriceByProductId: new Map(),
});

// Discounted display amounts keep cent precision; only formatting decides how
// many decimals the user sees.
export const roundToCents = (amount: number): number =>
  Math.round(amount * 100) / 100;

// Stripe applies a subscription's coupons sequentially in stack order —
// each one discounts the running total left by the ones before it — so this
// must fold left over the ordered stack rather than pick a single coupon.
export const applyCouponsToAmount = (
  amount: number,
  coupons:
    | readonly { percentOff?: number; amountOff?: number }[]
    | null
    | undefined
): number =>
  (coupons ?? []).reduce((runningAmount, coupon) => {
    if (coupon.percentOff != null) {
      return roundToCents(runningAmount * (1 - coupon.percentOff / 100));
    }
    if (coupon.amountOff != null) {
      return Math.max(0, roundToCents(runningAmount - coupon.amountOff / 100));
    }
    return runningAmount;
  }, amount);

// A flat coupon comes off every invoice, so its face value is per billing
// period. The page shows the live subscription as a monthly (or annual)
// equivalent, so the coupon must shrink by the same factor before it is
// applied: a $1,200 per-quarter coupon takes $400 off the monthly figure.
// Percentage coupons are cadence-invariant and pass through untouched.
export const scaleFlatCouponsToCadence = <
  Coupon extends { percentOff?: number; amountOff?: number },
>(
  coupons: readonly Coupon[],
  subscription: { interval: string; intervalCount: number } | undefined,
  cadence: BillingCycle
): readonly Coupon[] => {
  if (!subscription) {
    return coupons;
  }
  const factor =
    (cadence === "year" ? 12 : 1) /
    monthsPerPeriod(subscription.interval, subscription.intervalCount);
  if (factor === 1) {
    return coupons;
  }
  return coupons.map((coupon) =>
    coupon.amountOff == null
      ? coupon
      : { ...coupon, amountOff: coupon.amountOff * factor }
  );
};

export const getEligibleModuleCount = (modules: NamedBillingEntity[]): number =>
  resolveModularBundleDiscount(toBundleProducts(modules)).eligibleModuleCount;

export const getBundleDiscountPct = ({
  isUnlimited,
  modules,
}: {
  isUnlimited?: boolean;
  modules: NamedBillingEntity[];
}): number =>
  isUnlimited
    ? 0
    : resolveModularBundleDiscount(toBundleProducts(modules)).percentage * 100;

export const getNextTierBundleDiscountPct = (
  currentEligibleModuleCount: number
): number =>
  getModularBundleDiscountPercentageForModuleCount(
    currentEligibleModuleCount + 1
  ) * 100;

// Percentage discounts stack the way `applyCouponsToAmount` applies them: each
// one discounts what the ones before it left. A combined headline percentage
// must therefore compound them, never sum them — 20% + 10% + 20% is 42% off,
// not 50%.
export const compoundSavingsPct = (percentages: readonly number[]): number =>
  Math.round(
    (1 -
      percentages.reduce(
        (multiplier, pct) => multiplier * (1 - pct / 100),
        1
      )) *
      100
  );

export const getEffectiveSavingsPct = ({
  bundleDiscountPercentage = 0,
  annualDiscountPercentage = 0,
}: {
  bundleDiscountPercentage?: number;
  annualDiscountPercentage?: number;
}): number =>
  compoundSavingsPct([bundleDiscountPercentage, annualDiscountPercentage]);

export const getUnlimitedSavings = (
  fullModulesTotal: number,
  unlimitedPrice: number
): { savings: number; pct: number } => {
  const savings = fullModulesTotal - unlimitedPrice;
  const pct =
    fullModulesTotal > 0 ? Math.ceil((savings / fullModulesTotal) * 100) : 0;

  return { savings, pct };
};

export const getUnlimitedUpgradeSavings = ({
  activeModulesTotal,
  fullModulesTotal,
  unlimitedPrice,
}: {
  activeModulesTotal: number;
  fullModulesTotal: number;
  unlimitedPrice: number;
}): { savings: number; pct: number } => {
  const remainingModulesTotal = Math.max(
    0,
    fullModulesTotal - activeModulesTotal
  );
  if (remainingModulesTotal === 0) {
    return { savings: 0, pct: 0 };
  }

  const incrementalUnlimitedPrice = Math.max(
    0,
    unlimitedPrice - activeModulesTotal
  );
  const savings = Math.max(
    0,
    remainingModulesTotal - incrementalUnlimitedPrice
  );
  const pct =
    savings > 0
      ? Math.min(100, Math.ceil((savings / remainingModulesTotal) * 100))
      : 0;

  return { savings, pct };
};

// The billed amount for one entity at the requested cadence: annual rows use
// the direct interval price; a missing interval row falls back to the monthly
// equivalent rather than dropping the entity from totals.
export const getEntityCadencePrice = (
  entity: { currentPrice: number; intervalPrice?: number },
  cadence: BillingCycle
): number =>
  cadence === "year"
    ? (entity.intervalPrice ?? entity.currentPrice)
    : entity.currentPrice;

export const getSelectionTotals = (
  entities: CatalogEntities,
  selectedIds: string[],
  cadence: BillingCycle
): {
  selectedEntities: (BillingPlan | BillingModule)[];
  selectionSum: number;
  monthlyTotal: number;
  intervalTotal: number;
  bundleDiscountPct: number;
  effectiveSavingsPct: number;
  calculation: QuoteCalculation;
} => {
  const selectedIdSet = new Set(selectedIds);
  const isUnlimitedSelected = selectedIdSet.has(UNLIMITED_ID);
  const selectedEntities = [...entities.plans, ...entities.modules].filter(
    (entity) =>
      selectedIdSet.has(entity.id) &&
      (!isUnlimitedSelected || entity.type !== "module")
  );
  const monthlyEquivalentSum = selectedEntities.reduce(
    (sum, entity) => sum + entity.currentPrice,
    0
  );
  // The interval total sums the direct catalog rows for the requested cadence
  // (real annual price-card amounts), never a synthetic monthly ×12.
  const selectionSum = selectedEntities.reduce(
    (sum, entity) => sum + getEntityCadencePrice(entity, cadence),
    0
  );
  const bundleDiscountPct = getBundleDiscountPct({
    isUnlimited: isUnlimitedSelected,
    modules: selectedEntities,
  });
  // Discount the total once and keep cent precision so the quoted total is
  // exactly the sum of the per-line discounted display prices.
  const discountMultiplier = 1 - bundleDiscountPct / 100;
  const discountedIntervalBeforeRounding =
    bundleDiscountPct > 0 ? selectionSum * discountMultiplier : selectionSum;
  const intervalTotal =
    bundleDiscountPct > 0
      ? roundToCents(discountedIntervalBeforeRounding)
      : discountedIntervalBeforeRounding;
  const discountedMonthlyBeforeRounding =
    bundleDiscountPct > 0
      ? monthlyEquivalentSum * discountMultiplier
      : monthlyEquivalentSum;
  const monthlyTotal =
    bundleDiscountPct > 0
      ? roundToCents(discountedMonthlyBeforeRounding)
      : discountedMonthlyBeforeRounding;
  const calculation: QuoteCalculation = {
    bundleDiscountPct,
    discountedBeforeRounding:
      cadence === "year"
        ? discountedIntervalBeforeRounding
        : discountedMonthlyBeforeRounding,
    finalAmount: cadence === "year" ? intervalTotal : monthlyTotal,
    lines: selectedEntities.map((entity) => ({
      amount: getEntityCadencePrice(entity, cadence),
      entityId: entity.id,
      name: entity.name,
    })),
    undiscountedTotal: cadence === "year" ? selectionSum : monthlyEquivalentSum,
  };

  return {
    selectedEntities,
    selectionSum,
    monthlyTotal,
    intervalTotal,
    bundleDiscountPct,
    // Annual carts compound the marketing annual discount into the savings
    // story; monthly carts save the bundle discount alone.
    effectiveSavingsPct: getEffectiveSavingsPct({
      bundleDiscountPercentage: bundleDiscountPct,
      annualDiscountPercentage:
        cadence === "year" ? STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE : 0,
    }),
    calculation,
  };
};

export const getCurrentSelection = (snapshot: BillingSnapshot): Selection => {
  const cadence = currentCadence(snapshot);
  const catalog = buildBillingCatalog(catalogSnapshot(snapshot), cadence);

  return {
    planId: catalog.plans.some(
      ({ id, isCurrent }) => id === UNLIMITED_ID && isCurrent
    )
      ? UNLIMITED_ID
      : undefined,
    moduleIds: catalog.modules
      .filter(({ isActive }) => isActive)
      .map(({ id }) => id),
    cadence,
  };
};

// A trialing module bills $0, so it is not yet committed — unless its trial
// converts to paid, in which case it is already part of the plan and stays
// committed like any other active module.
export const getCommittedSelection = (snapshot: BillingSnapshot): Selection => {
  const current = getCurrentSelection(snapshot);
  const catalog = buildBillingCatalog(
    catalogSnapshot(snapshot),
    current.cadence
  );
  const trialingModuleIds = new Set(
    catalog.modules
      .filter(
        ({ isActive, trialEndAt, trialConverts }) =>
          isActive && trialEndAt !== undefined && !trialConverts
      )
      .map(({ id }) => id)
  );

  return trialingModuleIds.size === 0
    ? current
    : {
        ...current,
        moduleIds: current.moduleIds.filter((id) => !trialingModuleIds.has(id)),
      };
};

const totalsFor = (
  snapshot: BillingSnapshot,
  selection: Selection
): {
  totals: Omit<Quote, "deltaVsCurrent" | "proration">;
  plans: BillingPlan[];
  modules: BillingModule[];
} => {
  const { plans, modules } = buildBillingCatalog(
    catalogSnapshot(snapshot),
    selection.cadence
  );
  const {
    monthlyTotal,
    intervalTotal,
    bundleDiscountPct,
    effectiveSavingsPct,
    calculation,
  } = getSelectionTotals(
    { plans, modules },
    selection.planId
      ? [selection.planId, ...selection.moduleIds]
      : selection.moduleIds,
    selection.cadence
  );

  return {
    totals: {
      monthlyTotal,
      intervalTotal,
      bundleDiscountPct,
      effectiveSavingsPct,
      calculation,
    },
    plans,
    modules,
  };
};

const dollarsToCents = (dollars: number): number => Math.round(dollars * 100);

const accessProductsForModule = (
  module: BillingModule,
  accessProducts: AccessProduct[]
): AccessProduct[] => {
  const keys = new Set([module.id, ...(module.variantProductKeys ?? [])]);
  return accessProducts.filter(
    ({ entitlementProfileKey }) =>
      entitlementProfileKey != null && keys.has(entitlementProfileKey)
  );
};

const toCreditItems = (
  items: SubscriptionItem[]
): StripeProrationItemInput[] | null => {
  const creditItems: StripeProrationItemInput[] = [];
  for (const item of items.filter(({ trialing }) => !trialing)) {
    if (item.amountCents == null) {
      return null;
    }
    creditItems.push({ amountCents: item.amountCents, priceId: item.priceId });
  }

  return creditItems;
};

const buildBillingAdjustment = ({
  snapshot,
  selection,
  plans,
  modules,
  intervalTotal,
  nowSec,
}: {
  snapshot: BillingSnapshot;
  selection: Selection;
  plans: BillingPlan[];
  modules: BillingModule[];
  intervalTotal: number;
  nowSec: number;
}): BillingAdjustment | null => {
  const subscriptionAccessData = snapshot.subscriptionAccessData;
  // The subscription Stripe will actually prorate against — the same one the
  // planner will mutate (`planSelectionChange` → `currentLiveSubscription`), so
  // the money quoted in the dialog and the money Stripe raises come off one row.
  // A literal `.find` took array order instead, and `subscriptions.active`
  // arrives unsorted, so an org carrying a leftover `past_due` sub alongside its
  // active one could be quoted against the row nothing was changing.
  //
  // `trialing` still yields no adjustment — its items are all trialing, so there
  // is nothing to credit and no charge to estimate — but that is decided after
  // the primary is chosen rather than by excluding it from the search, which is
  // what keeps this selector and the planner's the same selector.
  const targetSubscription = currentLiveSubscription(snapshot);
  if (!targetSubscription || targetSubscription.status === "trialing") {
    return null;
  }
  if (
    targetSubscription.billingProvider != null &&
    targetSubscription.billingProvider !== "stripe"
  ) {
    return null;
  }

  const items = targetSubscription.items;
  if (!items || items.length === 0) {
    return null;
  }
  if (targetSubscription.coupon?.amountOff != null) {
    return null;
  }
  if (targetSubscription.interval !== selection.cadence) {
    if (
      targetSubscription.interval !== "month" ||
      selection.cadence !== "year" ||
      intervalTotal <= 0
    ) {
      return null;
    }
    const creditItems = toCreditItems(items);
    if (creditItems == null || creditItems.length === 0) {
      return null;
    }
    const creditResult = computeStripeProration({
      oldItems: creditItems,
      newItems: [],
      oldCouponPercent: targetSubscription.coupon?.percentOff ?? 0,
      newCouponPercent: 0,
      periodStartSec: targetSubscription.periodStart,
      periodEndSec: targetSubscription.periodEnd,
      prorationDateSec: nowSec,
      prorationBehavior: DEFAULT_STRIPE_PRORATION_BEHAVIOR,
    });
    if (
      creditResult.netCents == null ||
      creditResult.netCents >= 0 ||
      creditResult.appliedAt === "none" ||
      creditResult.secondsTotal <= 0 ||
      creditResult.secondsRemaining <= 0
    ) {
      return null;
    }
    const unusedCurrentCycleCredit = Math.abs(creditResult.netCents) / 100;

    return {
      kind: "billing-cycle-reset",
      ...prorationDayDisplay(creditResult),
      fullNewIntervalCharge: intervalTotal,
      unusedCurrentCycleCredit,
      netDueToday: roundToCents(intervalTotal - unusedCurrentCycleCredit),
      chargedToday: true,
      appliedAt: "immediate",
    };
  }

  const accessProducts = subscriptionAccessData.access.products;
  const trialingPriceIds = new Set(
    items.filter(({ trialing }) => trialing).map(({ priceId }) => priceId)
  );
  const currentPlanId = plans.find(({ isCurrent }) => isCurrent)?.id;
  const isUpgradeToUnlimited =
    selection.planId === UNLIMITED_ID && currentPlanId !== UNLIMITED_ID;
  let oldItems: StripeProrationItemInput[];
  let newItems: StripeProrationItemInput[];
  let newCouponPercent: number;

  if (isUpgradeToUnlimited) {
    const creditItems = toCreditItems(items);
    const unlimitedPlan = plans.find(({ id }) => id === UNLIMITED_ID);
    if (
      creditItems == null ||
      !unlimitedPlan?.intervalPrice ||
      unlimitedPlan.intervalPrice <= 0
    ) {
      return null;
    }
    oldItems = creditItems;
    newItems = [{ amountCents: dollarsToCents(unlimitedPlan.intervalPrice) }];
    newCouponPercent = 0;
  } else {
    const activeModuleIds = new Set(
      modules.filter(({ isActive }) => isActive).map(({ id }) => id)
    );
    const addedModules = selection.moduleIds
      .filter((id) => !activeModuleIds.has(id))
      .map((id) => modules.find((module) => module.id === id));
    const removedModules = modules.filter(
      ({ id, isActive }) => isActive && !selection.moduleIds.includes(id)
    );
    if (addedModules.length === 0 && removedModules.length === 0) {
      return null;
    }

    newItems = [];
    for (const module of addedModules) {
      if (!module?.intervalPrice || module.intervalPrice <= 0) {
        return null;
      }
      newItems.push({
        amountCents: dollarsToCents(module.intervalPrice),
        priceId: module.stripePriceId,
      });
    }

    oldItems = [];
    for (const module of removedModules) {
      const productPriceIds = new Set(
        accessProductsForModule(module, accessProducts).flatMap(
          ({ priceIds }) => priceIds
        )
      );
      const matchedItems = items.filter(({ priceId }) =>
        productPriceIds.has(priceId)
      );
      if (matchedItems.length === 0) {
        return null;
      }
      const creditItems = toCreditItems(matchedItems);
      if (creditItems == null) {
        return null;
      }
      oldItems.push(...creditItems);
    }

    // Classify by entitlement profile key (id), never name alone: a name-only
    // product misreads "Smart Pricing Unlimited" as the Unlimited plan and
    // drops it from the eligible-module count, so the proration coupon would
    // diverge from the quoted bundle discount (see toBundleProducts).
    const bundleProducts: ModularBundleDiscountProduct[] = [];
    const selectedPlan = plans.find(({ id }) => id === selection.planId);
    if (selectedPlan) {
      bundleProducts.push({
        name: selectedPlan.name,
        entitlementProfileKey: selectedPlan.id,
      });
    }
    for (const id of selection.moduleIds) {
      const module = modules.find((candidate) => candidate.id === id);
      if (!module) {
        continue;
      }
      const isTrialing = accessProductsForModule(module, accessProducts).some(
        ({ priceIds }) =>
          priceIds.some((priceId) => trialingPriceIds.has(priceId))
      );
      if (!isTrialing) {
        bundleProducts.push({
          name: module.name,
          entitlementProfileKey: module.id,
        });
      }
    }
    newCouponPercent = Math.round(
      resolveModularBundleDiscount(bundleProducts).percentage * 100
    );
  }

  const result = computeStripeProration({
    oldItems,
    newItems,
    oldCouponPercent: targetSubscription.coupon?.percentOff ?? 0,
    newCouponPercent,
    periodStartSec: targetSubscription.periodStart,
    periodEndSec: targetSubscription.periodEnd,
    prorationDateSec: nowSec,
    prorationBehavior: DEFAULT_STRIPE_PRORATION_BEHAVIOR,
  });
  if (
    result.netCents == null ||
    result.appliedAt === "none" ||
    result.secondsTotal <= 0 ||
    result.secondsRemaining <= 0
  ) {
    return null;
  }

  return {
    kind: "proration",
    ...prorationDayDisplay(result),
    netAmount: result.netCents / 100,
    chargedToday: result.chargedToday,
    appliedAt: result.appliedAt,
  };
};

export const quoteSelection = (
  snapshot: BillingSnapshot,
  selection: Selection,
  nowSec?: number
): Quote => {
  const { totals, plans, modules } = totalsFor(snapshot, selection);
  const currentTotals = totalsFor(
    snapshot,
    getCommittedSelection(snapshot)
  ).totals;
  const quote: Quote = {
    ...totals,
    deltaVsCurrent: {
      monthly: roundToCents(totals.monthlyTotal - currentTotals.monthlyTotal),
      interval: roundToCents(
        totals.intervalTotal - currentTotals.intervalTotal
      ),
    },
  };

  if (nowSec !== undefined) {
    const billingAdjustment = buildBillingAdjustment({
      snapshot,
      selection,
      plans,
      modules,
      intervalTotal: totals.intervalTotal,
      nowSec,
    });
    quote.billingAdjustment = billingAdjustment
      ? { ...billingAdjustment, asOf: nowSec }
      : null;
  }

  return quote;
};

export const quoteCurrentSelection = (snapshot: BillingSnapshot): Quote =>
  quoteSelection(snapshot, getCommittedSelection(snapshot));

const moduleForPath = (
  snapshot: BillingSnapshot,
  path: RawUpgradeDowngradePath,
  modules: BillingModule[]
): BillingModule | undefined => {
  const direct = modules.find(
    ({ stripePriceId }) =>
      stripePriceId != null && path.target.priceIds.includes(stripePriceId)
  );
  if (direct) {
    return direct;
  }

  const targetProductIds = new Set(
    snapshot.livePrices
      .filter(
        (price) =>
          path.target.priceIds.includes(price.id) ||
          price.priceIds.some((id) => path.target.priceIds.includes(id))
      )
      .map(({ product }) => product.id)
  );

  return modules.find(({ stripePriceId }) => {
    const price = snapshot.livePrices.find(({ id }) => id === stripePriceId);
    return price != null && targetProductIds.has(price.product.id);
  });
};

const selectionAfterAction = (
  snapshot: BillingSnapshot,
  path: RawUpgradeDowngradePath,
  baseline: Selection,
  modules: BillingModule[]
): Selection | undefined => {
  if (path.changeType === "upgrade_to_blue") {
    return { ...baseline, planId: UNLIMITED_ID };
  }

  const module = moduleForPath(snapshot, path, modules);
  if (!module) {
    return undefined;
  }
  if (path.changeType === "remove_module") {
    return {
      ...baseline,
      moduleIds: baseline.moduleIds.filter((id) => id !== module.id),
    };
  }

  const moduleIds = SMART_PRICING_VARIANTS.has(module.id)
    ? [
        ...baseline.moduleIds.filter((id) => !SMART_PRICING_VARIANTS.has(id)),
        module.id,
      ]
    : [...new Set([...baseline.moduleIds, module.id])];

  return { ...baseline, moduleIds };
};

export const getActionQuotes = (
  snapshot: BillingSnapshot
): Record<string, ActionQuote> => {
  const { eligible, deferred, blocked } =
    snapshot.subscriptionAccessData.upgradeDowngradePaths;
  const baseline = getCurrentSelection(snapshot);
  const { modules } = buildBillingCatalog(
    catalogSnapshot(snapshot),
    baseline.cadence
  );
  const quotes: Record<string, ActionQuote> = {};

  for (const path of [...eligible, ...deferred, ...blocked]) {
    const selection = selectionAfterAction(snapshot, path, baseline, modules);
    if (!selection) {
      continue;
    }
    quotes[path.id] = {
      pathId: path.id,
      changeType: path.changeType,
      decision: path.decision,
      quote: quoteSelection(snapshot, selection),
    };
  }

  return quotes;
};
